import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxDataGridComponent, DxDataGridModule, DxDropDownButtonModule, DxLoadPanelModule, DxPopupModule, DxButtonModule, DxTagBoxModule, DxTooltipComponent, DxTooltipModule } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import { CrudService } from '../services/crud.service';
import { ConfigService } from '../services/config.service';
import { Event as CalendarEvent, EventsService } from '../services/events.service';
import { environment } from 'src/environments/environment';
import { SlotTypes } from '../models/slot-types.model';
import { BuyingGroups } from '../models/buying-groups.model';
import { NotifyService } from '../services/notify.service';
import { catchError, throwError, firstValueFrom } from 'rxjs';
import { CreatePromotionComponent } from '../create-promotion/create-promotion.component';
import { DetailViewComponent } from '../detail-view/detail-view.component';
import { JoyrideModule } from 'ngx-joyride';
import { JourneyService } from '../services/journey.service';
import { PageHeaderComponent } from 'src/app/shared/page-header/page-header.component';

interface Product {
  product_code: string;
  product_name: string;
  supplier_code: string;
  supplier_name: string;
  host_product_group_code: string;
  host_product_group_description: string;
  buying_group_id: number;
  buying_group_code: string;
  buying_group_name: string;
  [key: string]: any; // For dynamic date columns
}

interface DateColumn {
  dataField: string;
  caption: string;
  dataType: string;
  showInColumnChooser: boolean;
  lookup?: {
    dataSource: SlotTypes[];
    valueExpr: string;
    displayExpr: string;
    allowClearing: boolean;
  };
}

enum SlotFilter {
  ShowAll = 0,
  Slotted = 1,
  NotSlotted = 2
}

type PeriodMode = 'week' | 'month';

@Component({
  selector: 'app-slotting-board-v2',
  imports: [CommonModule, DxDataGridModule, DxDropDownButtonModule, DxLoadPanelModule, DxPopupModule, DxButtonModule, DxTagBoxModule, DxTooltipModule, CreatePromotionComponent, DetailViewComponent, JoyrideModule, PageHeaderComponent],
  standalone: true,
  templateUrl: './slotting-board-v2.component.html',
  styleUrl: './slotting-board-v2.component.css'
})
export class SlottingBoardV2Component implements OnInit {
  @ViewChild('slottingGrid', { static: false }) dataGrid: DxDataGridComponent;
  @ViewChild('eventTooltip', { static: false }) eventTooltip: DxTooltipComponent;

  dataSource: any;
  dynamicColumns: DateColumn[] = [];
  slotTypes: SlotTypes[] = [];
  buyingGroups: BuyingGroups[] = [];
  promotionTypes: any[] = [];
  slottingBoardData: any[] = [];
  events: CalendarEvent[] = [];
  eventTooltipData: CalendarEvent[] = [];
  eventTooltipTarget: any = null;
  private apiUrl: string = environment.baseApiUrl;
  currentEditColumn: string | null = null;
  loading: boolean = false;
  createPromotion: boolean = false;
  startPeriodOffset: number = 0; // Months (monthly mode) or weeks (weekly mode) from today; weekly mode starts at -4 (set in resolveMode)
  toolbarInitialized: boolean = false;
  showFilterRow: boolean = false;
  periodMode: PeriodMode = 'month';
  weekStartIndex: number = 0;
  // ISO start date (YYYY-MM-DD) keyed by weekly column dataField — sent to the API on save.
  private columnStartDates: { [dataField: string]: string } = {};
  private storageKey: string = 'slottingBoardV2State:month';
  // Buying groups the board is currently scoped to. Sent to the API as buying_group_ids[].
  selectedBuyingGroupIds: number[] = [];
  // Mode-independent (weekly and monthly share one selection) — unlike the grid state key.
  private buyingGroupStorageKey: string = 'slottingBoardV2BuyingGroups';
  slotFilterItems = [
    { id: SlotFilter.ShowAll, name: 'Show All' },
    { id: SlotFilter.Slotted, name: 'Slotted' },
    { id: SlotFilter.NotSlotted, name: 'Not Slotted' }
  ];
  currentSlotFilter = this.slotFilterItems[0];
  private pendingFilterReapply: boolean = false;

  constructor(
    private crudService: CrudService,
    private configService: ConfigService,
    private eventsService: EventsService,
    private notifyService: NotifyService,
    private journeyService: JourneyService
  ) {}

  async ngOnInit() {
    this.loading = true;
    await this.resolveMode();
    await this.loadSlotTypes();
    await this.loadBuyingGroups();
    this.resolveInitialBuyingGroupSelection();
    await this.loadPromotionTypes();
    if (this.periodMode === 'week') {
      await this.loadEvents();
    }
    // Schedule restoration of saved DevExtreme grid state (filters, widths,
    // static-column order). Note: loadState is not actually async — it schedules a
    // setTimeout(300) that calls dataGrid.instance.state(...), so generateColumns()
    // below runs first and state is reconciled against those columns ~300ms later.
    // Also note: startPeriodOffset is intentionally NOT persisted — each session
    // starts at the mode's default offset anchored on today (0 monthly, -4 weekly).
    await this.loadState();
    this.generateColumns();
    this.initializeDataSource();
  }

  // Fetch holidays + events for a range wide enough to cover backward/forward nav.
  // Covers roughly last year through next two years so both the default 13-week window
  // and multiple nav clicks have data available without re-fetching.
  private async loadEvents(): Promise<void> {
    try {
      const today = new Date();
      const startDate = (today.getFullYear() - 1) * 10000 + 101; // Jan 1 of last year
      const endDate = (today.getFullYear() + 2) * 10000 + 1231;  // Dec 31 two years out
      this.events = (await this.eventsService.getEvents(startDate, endDate)) ?? [];
    } catch (error) {
      console.error('Error loading events for slotting board:', error);
      this.events = [];
    }
  }

  // True when any system event (holiday, editable=0) falls inside the week column.
  hasSystemEvent(dataField: string): string {
    const start = this.columnStartDates[dataField];
    if (!start) return 'hidden';
    return this.events.some(e => e.editable === 0 && this.eventFallsInWeek(e, start))
      ? 'visible' : 'hidden';
  }

  // True when any custom event (editable=1) falls inside the week column.
  hasCustomEvent(dataField: string): string {
    const start = this.columnStartDates[dataField];
    if (!start) return 'hidden';
    return this.events.some(e => e.editable === 1 && this.eventFallsInWeek(e, start))
      ? 'visible' : 'hidden';
  }

  // Events whose date is within [weekStart, weekStart+6], used by the tooltip.
  private getWeekEvents(dataField: string): CalendarEvent[] {
    const start = this.columnStartDates[dataField];
    if (!start) return [];
    return this.events.filter(e => this.eventFallsInWeek(e, start));
  }

  private eventFallsInWeek(event: CalendarEvent, weekStartIso: string): boolean {
    const eventDateNum = typeof event.date === 'number'
      ? event.date
      : parseInt(String(event.date), 10);
    if (!Number.isFinite(eventDateNum)) return false;

    const [y, m, d] = weekStartIso.split('-').map(Number);
    const startNum = y * 10000 + m * 100 + d;
    const endDate = new Date(weekStartIso);
    endDate.setDate(endDate.getDate() + 6);
    const endNum = endDate.getFullYear() * 10000
      + (endDate.getMonth() + 1) * 100
      + endDate.getDate();
    return eventDateNum >= startNum && eventDateNum <= endNum;
  }

  // DD-MM-YYYY from YYYYMMDD integer (matches V1 data-grid.formatDate output).
  formatEventDate(date: number | string): string {
    const n = typeof date === 'number' ? date : parseInt(String(date), 10);
    if (!Number.isFinite(n)) return String(date);
    const y = Math.floor(n / 10000);
    const m = Math.floor((n % 10000) / 100);
    const d = n % 100;
    return `${d.toString().padStart(2, '0')}-${m.toString().padStart(2, '0')}-${y}`;
  }

  // Triggered on hover of any cell — show the event tooltip for weekly headers that
  // contain at least one event. Monthly mode and non-header cells are ignored.
  onCellHoverChanged = (e: any) => {
    if (this.periodMode !== 'week' || e.rowType !== 'header') return;
    const dataField = e.column?.dataField;
    if (!dataField || !dataField.startsWith('week_')) return;

    if (e.eventType === 'mouseover') {
      const weekEvents = this.getWeekEvents(dataField);
      if (weekEvents.length > 0) {
        this.eventTooltipData = weekEvents;
        this.eventTooltipTarget = e.cellElement;
        this.eventTooltip?.instance.show(e.cellElement);
      }
    } else if (e.eventType === 'mouseout') {
      this.eventTooltip?.instance.hide();
    }
  }

  get backwardLabel(): string {
    return this.periodMode === 'week' ? 'Previous 13 Weeks' : 'Previous 12 Months';
  }

  get forwardLabel(): string {
    return this.periodMode === 'week' ? 'Next 13 Weeks' : 'Next 12 Months';
  }

  // Resolve period mode and week-start offset from config before any column/data work.
  private async resolveMode(): Promise<void> {
    try {
      const slottingTypeData = (await this.configService.GetConfigByKey('slottingType')) ?? [];
      const weekStartData = (await this.configService.GetConfigByKey('WeekStartIndex')) ?? [];

      const slottingType = slottingTypeData.length > 0 ? slottingTypeData[0].config_string : null;
      this.periodMode = slottingType === '2' ? 'week' : 'month';

      const rawWeekStart = weekStartData.length > 0 ? parseInt(weekStartData[0].config_string, 10) : 0;
      this.weekStartIndex = Number.isFinite(rawWeekStart) ? Math.min(6, Math.max(0, rawWeekStart)) : 0;
    } catch (error) {
      console.error('Error resolving slotting board mode:', error);
      this.periodMode = 'month';
      this.weekStartIndex = 0;
    }

    // Weekly boards open 4 weeks back so recent history is visible without navigating;
    // monthly boards still open at the current month.
    this.startPeriodOffset = this.periodMode === 'week' ? -4 : 0;

    this.storageKey = `slottingBoardV2State:${this.periodMode}`;

    // One-time migration: existing monthly users' persisted state lives under the legacy key.
    if (this.periodMode === 'month'
        && !localStorage.getItem(this.storageKey)
        && localStorage.getItem('slottingBoardV2State')) {
      localStorage.setItem(this.storageKey, localStorage.getItem('slottingBoardV2State')!);
      localStorage.removeItem('slottingBoardV2State');
    }
  }

  // Load buying groups from API
  async loadBuyingGroups() {
    try {
      this.buyingGroups = await firstValueFrom(this.crudService.getData(`${this.apiUrl}buying-groups`));
    } catch (error) {
      console.error('Error loading buying groups:', error);
      this.buyingGroups = [];
    }
  }

  // Resolve which buying groups the board starts scoped to.
  // Restore a persisted selection if present (dropping any IDs that no longer exist);
  // otherwise default to the buying group(s) flagged default, falling back to all so the
  // board is never empty on first visit.
  private resolveInitialBuyingGroupSelection(): void {
    const validIds = new Set(this.buyingGroups.map(bg => bg.id));

    const saved = localStorage.getItem(this.buyingGroupStorageKey);
    if (saved !== null) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          this.selectedBuyingGroupIds = parsed
            .map((id: any) => Number(id))
            .filter((id: number) => validIds.has(id));
          return;
        }
      } catch {
        // fall through to default
      }
    }

    const defaults = this.buyingGroups.filter(bg => bg.default).map(bg => bg.id);
    this.selectedBuyingGroupIds = defaults.length > 0
      ? defaults
      : this.buyingGroups.map(bg => bg.id);
  }

  // Function to toggle the filter row visibility
  toggleFilterRow(): void {
    this.showFilterRow = !this.showFilterRow;
    // Only clear the filter row values, not column-level filters (preserves slot filter)
    this.dataGrid.instance.clearFilter('row');
  }

  // Load slot types from API
  async loadSlotTypes() {
    try {
      const slotTypesData: SlotTypes[] = await firstValueFrom(this.crudService.getData(`${this.apiUrl}slot-types`));
      // Add codeName property combining code and name
      this.slotTypes = slotTypesData.map((slot) => {
        const codeName = slot.code + ' - ' + slot.name;
        return { ...slot, codeName };
      });
    } catch (error) {
      console.error('Error loading slot types:', error);
      this.slotTypes = [];
    }
  }

  // Load promotion types from API
  async loadPromotionTypes() {
    try {
      this.promotionTypes = await firstValueFrom(this.crudService.getData(`${this.apiUrl}promotion-types`));
    } catch (error) {
      console.error('Error loading promotion types:', error);
      this.promotionTypes = [];
    }
  }

  // Generate columns based on period mode.
  generateColumns() {
    this.loading = true;
    this.dynamicColumns = [];
    this.columnStartDates = {};

    if (this.periodMode === 'week') {
      const today = new Date();
      const baseStart = this.getWeekStart(today, this.weekStartIndex);
      for (let i = 0; i < 52; i++) {
        const start = new Date(baseStart);
        start.setDate(start.getDate() + (this.startPeriodOffset + i) * 7);
        const { isoYear, isoWeek } = this.getIsoYearWeek(start);
        const dayMonth = `${start.getDate()}/${start.getMonth() + 1}`;
        const caption = `W${isoWeek}\n${dayMonth}`;
        const dataField = `week_${isoYear}_${isoWeek}`;
        this.columnStartDates[dataField] = this.toIsoDate(start);
        this.dynamicColumns.push({
          dataField,
          caption,
          dataType: 'string',
          showInColumnChooser: false,
          lookup: {
            dataSource: this.slotTypes,
            valueExpr: 'code',
            displayExpr: 'codeName',
            allowClearing: true
          }
        });
      }
      this.loading = false;
      return;
    }

    const currentDate = new Date();
    // Generate 24 months starting from the offset
    for (let i = 0; i < 24; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + this.startPeriodOffset + i, 1);
      const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
      const year = date.getFullYear().toString();
      const caption = `${month}\n${year}`; // Line break for wrapping
      const dataField = `month_${date.getFullYear()}_${date.getMonth() + 1}`;

      this.dynamicColumns.push({
        dataField: dataField,
        caption: caption,
        dataType: 'string',
        showInColumnChooser: false,
        lookup: {
          dataSource: this.slotTypes,
          valueExpr: 'code',
          displayExpr: 'codeName',
          allowClearing: true
        }
      });
    }
    this.loading = false;
  }

  // Navigate backward one visible window (12 months or 13 weeks)
  navigateBackward() {
    this.navigatePeriods(-(this.periodMode === 'week' ? 13 : 12));
  }

  // Navigate forward one visible window
  navigateForward() {
    this.navigatePeriods(this.periodMode === 'week' ? 13 : 12);
  }

  // Shift the visible window and rebuild columns + data. Regenerating the dxi-column
  // collection makes DevExtreme rebuild its columns and drop runtime state (filters,
  // sorting, static-column widths/order), so snapshot the state first and re-apply it —
  // minus the period columns, whose dataFields/order have changed — once the new columns
  // render (same 300ms deferral as loadState).
  private navigatePeriods(delta: number) {
    this.loading = true;
    this.toolbarInitialized = false;
    const state = this.dataGrid?.instance.state();
    this.startPeriodOffset += delta;
    this.generateColumns();
    this.initializeDataSource();
    if (state) {
      this.stripPeriodColumnsFromState(state);
      setTimeout(() => this.dataGrid?.instance.state(state), 300);
    }
  }


  // Build the buying-group filter as a URL query string. Always sent so the API applies
  // the filter: a CSV of selected IDs, or a single empty value (present-but-empty = no
  // buying groups). Appended to the URL directly because crudService.getData's params arg
  // only forwards DevExtreme LoadOptions keys.
  private buyingGroupQueryString(): string {
    return '?buying_group_ids[]=' + this.selectedBuyingGroupIds.join('&buying_group_ids[]=');
  }

  // Buying-group multi-select changed — persist the selection and re-fetch.
  // Columns are unchanged, so no generateColumns() call is needed. Ignores no-op changes
  // (same set) so the initial programmatic [value] binding doesn't trigger a redundant load.
  onBuyingGroupSelectionChanged(e: any) {
    const next: number[] = e.value ?? [];
    const unchanged = next.length === this.selectedBuyingGroupIds.length
      && next.every(id => this.selectedBuyingGroupIds.includes(id));
    if (unchanged) return;

    this.selectedBuyingGroupIds = next;
    localStorage.setItem(this.buyingGroupStorageKey, JSON.stringify(this.selectedBuyingGroupIds));
    this.loading = true;
    this.initializeDataSource();
  }

  // Initialize the data source with CustomStore
  initializeDataSource() {
    this.loading = true;
    const key = this.periodMode === 'week'
      ? ['promotion_group_id', 'buying_group_id']
      : ['product_code', 'buying_group_id', 'supplier_code'];

    this.dataSource = new CustomStore<Product>({
      key,
      load: async () => {
        return await new Promise((resolve, reject) => {
          const bgQs = this.buyingGroupQueryString();
          const rosterUrl = (this.periodMode === 'week'
            ? `${this.apiUrl}slotting-board-promotion-groups`
            : `${this.apiUrl}planning-data-products`) + bgQs;

          // Load both rows and slot assignments
          Promise.all([
            firstValueFrom(this.crudService.getData(rosterUrl)),
            firstValueFrom(this.crudService.getData(`${this.apiUrl}slotting-board-data${bgQs}`))
          ]).then(([rosterResult, slotAssignments]) => {
            // Transform the data to include dynamic columns with default values
            const rows = (rosterResult as any).data || rosterResult;
            const slots = (slotAssignments as any).data || slotAssignments || [];

            // Store slotting board data for use in create-promotion component
            this.slottingBoardData = slots;

            const transformedRows = rows.map((row: any) => {
              const rowWithDates: any = this.periodMode === 'week'
                ? {
                    promotion_group_id: row.promotion_group_id,
                    promotion_group_name: row.promotion_group_name,
                    product_group_code: row.product_group_code,
                    product_group_name: row.product_group_name,
                    supplier_code: row.supplier_code,
                    supplier_name: row.supplier_name,
                    buying_group_id: row.buying_group_id,
                    buying_group_code: row.buying_group_code,
                    buying_group_name: row.buying_group_name,
                    ...row
                  }
                : {
                    product_code: row.product_code,
                    product_name: row.product_name,
                    supplier_code: row.supplier_code,
                    supplier_name: row.supplier_name,
                    host_product_group_code: row.host_product_group_code,
                    host_product_group_description: row.host_product_group_description,
                    buying_group_id: row.buying_group_id,
                    buying_group_code: row.buying_group_code,
                    buying_group_name: row.buying_group_name,
                    ...row
                  };

              // Initialize all dynamic columns with null
              this.dynamicColumns.forEach(col => {
                rowWithDates[col.dataField] = null;
              });

              // Populate slot assignments from backend
              slots.forEach((slot: any) => {
                const rowMatches = this.periodMode === 'week'
                  ? (slot.promotion_group_id === rowWithDates.promotion_group_id
                      && slot.buying_group_id === rowWithDates.buying_group_id)
                  : (slot.product_code === row.product_code
                      && slot.buying_group_id === row.buying_group_id
                      && slot.supplier_code === row.supplier_code);

                if (rowMatches) {
                  // Construct the column name from slot data (period info is nested)
                  const period = slot.period;
                  if (period && period.year && period.month) {
                    const columnName = this.periodMode === 'week'
                      ? `week_${period.year}_${period.month}`
                      : `month_${period.year}_${period.month}`;

                    if (rowWithDates.hasOwnProperty(columnName)) {
                      rowWithDates[columnName] = slot.slot_type_code;
                    }
                  }
                }
              });

              // Compute isSlotted — true if any visible period column has a value
              rowWithDates.isSlotted = this.dynamicColumns.some(
                col => rowWithDates[col.dataField] != null
              );

              return rowWithDates;
            });

            resolve(transformedRows);
            this.loading = false;
            this.pendingFilterReapply = true;
          }).catch((error: any) => {
            console.error('Error loading data:', error);
            reject(error);
          });
        });
      },
      update: async (key, values) => {
        return await new Promise((resolve, reject) => {
          // Update will be implemented when we add persistence
          console.log('Update called with key:', key, 'values:', values);
          resolve(values);
        });
      }
    });
  }


  onSlotFilterChanged(e: any) {
    const item = e.item ?? e.selectedItem;
    if (!item) return;
    this.currentSlotFilter = item;
    this.applySlotFilter(item);
  }

  private applySlotFilter(filterItem: { id: number; name: string }) {
    const grid = this.dataGrid?.instance;
    if (!grid) return;

    // Use column-level filterValue to avoid replacing user-applied filters
    switch (filterItem.id) {
      case SlotFilter.Slotted:
        grid.columnOption('isSlotted', 'filterValue', true);
        break;
      case SlotFilter.NotSlotted:
        grid.columnOption('isSlotted', 'filterValue', false);
        break;
      default:
        grid.columnOption('isSlotted', 'filterValue', undefined);
        break;
    }
  }

  // Get slot type color by code
  getSlotTypeColour(code: string): string {
    const slotType = this.slotTypes.find(type => type.code === code);
    return slotType ? slotType.colour : '';
  }

  // Get slot type name by code
  getSlotTypeName(code: string): string {
    const slotType = this.slotTypes.find(type => type.code === code);
    return slotType ? slotType.name : '';
  }

  // Track which column is being edited
  onEditingStart(e: any) {
    if (e.column && e.column.dataField) {
      this.currentEditColumn = e.column.dataField;
    }
  }

  // Customize editor options - set wider popup for lookup
  onEditorPreparing(e: any) {
    if (e.parentType === "dataRow" && e.dataField && this.isPeriodColumn(e.dataField)) {
      e.editorOptions.onOpened = function (ev: any) {
        ev.component._popup.option('width', 150);
      };
    }
  }

  // Remove save/revert buttons from the toolbar after grid is ready
  onContentReady(e: any) {
    // Re-apply slot filter after data reload (e.g. period navigation)
    if (this.pendingFilterReapply) {
      this.pendingFilterReapply = false;
      if (this.currentSlotFilter.id !== SlotFilter.ShowAll) {
        this.applySlotFilter(this.currentSlotFilter);
      }
    }

    // Only run this once to avoid removing toolbar items on subsequent renders
    if (this.toolbarInitialized) {
      this.loading = false;
      return;
    }

    this.journeyService.tryStartPhase('slot-products');

    // Remove the save and revert buttons from the toolbar
    const toolbarItems = e.component.option('toolbar.items');
    if (toolbarItems) {
      const filteredItems = toolbarItems.filter((item: any) =>
        item.name !== 'saveButton' && item.name !== 'revertButton'
      );
      // Only update if we actually removed items
      if (filteredItems.length !== toolbarItems.length) {
        e.component.option('toolbar.items', filteredItems);
        this.toolbarInitialized = true;
      }
    }
  }

  // setCellValue callback for batch mode - enables auto-save without save button
  setCellValue = (rowData: any, value: any, currentRowData: any) => {
    // In batch mode with setCellValue:
    // - rowData: the new data object being built (modified in place)
    // - value: the new slot type code selected (or null if cleared)
    // - currentRowData: the original row data

    // Use the tracked column from onEditingStart
    if (this.currentEditColumn && this.isPeriodColumn(this.currentEditColumn)) {
      // If value is null or empty, just clear this cell
      if (!value) {
        rowData[this.currentEditColumn] = null;
        const fullRowData = { ...currentRowData, ...rowData };
        this.autoSave(fullRowData, { [this.currentEditColumn]: null });
        this.currentEditColumn = null;
        return;
      }

      // Find the slot type to get the week_count (used as month_count in monthly mode)
      const slotType = this.slotTypes.find(type => type.code === value);
      const repeatCount = slotType?.week_count || 1;

      // Parse the current column to get year and period number
      const columnInfo = this.parseColumnName(this.currentEditColumn);
      if (!columnInfo) {
        this.currentEditColumn = null;
        return;
      }

      const changedColumns: any = {};

      if (this.periodMode === 'week') {
        // Walk forward one week at a time, re-deriving ISO (year, week) from the running date.
        const startKey = this.currentEditColumn;
        const startDate = this.columnStartDates[startKey];
        if (!startDate) {
          this.currentEditColumn = null;
          return;
        }
        const running = new Date(startDate);
        for (let i = 0; i < repeatCount; i++) {
          const { isoYear, isoWeek } = this.getIsoYearWeek(running);
          const columnName = `week_${isoYear}_${isoWeek}`;
          if (this.dynamicColumns.some(col => col.dataField === columnName)) {
            rowData[columnName] = value;
            changedColumns[columnName] = value;
          }
          running.setDate(running.getDate() + 7);
        }
      } else {
        let currentYear = columnInfo.year;
        let currentMonth = columnInfo.month;
        for (let i = 0; i < repeatCount; i++) {
          const columnName = `month_${currentYear}_${currentMonth}`;
          if (this.dynamicColumns.some(col => col.dataField === columnName)) {
            rowData[columnName] = value;
            changedColumns[columnName] = value;
          }
          currentMonth++;
          if (currentMonth > 12) {
            currentMonth = 1;
            currentYear++;
          }
        }
      }

      // Trigger auto-save with all changed columns
      const fullRowData = { ...currentRowData, ...rowData };
      this.autoSave(fullRowData, changedColumns);

      // Clear the tracked column
      this.currentEditColumn = null;
    }
  }

  // Handle row updates and trigger auto-save
  onRowUpdating(e: any) {
    // e.oldData = current row data
    // e.newData = changed values only
    const rowData = { ...e.oldData, ...e.newData };

    // Don't prevent the default behavior - let the grid update the cell immediately
    // Just trigger the save in the background
    this.autoSave(rowData, e.newData);
  }

  private recalcIsSlotted(rowData: any): boolean {
    return this.dynamicColumns.some(col => rowData[col.dataField] != null);
  }

  private isPeriodColumn(dataField: string): boolean {
    return this.periodMode === 'week'
      ? dataField.startsWith('week_')
      : dataField.startsWith('month_');
  }

  // Auto-save slot assignments
  async autoSave(rowData: any, changedData: any) {
    const updates: Promise<any>[] = [];

    // Get user details for group_id and location_id
    const userDetailsStr = localStorage.getItem('userDetails');
    const userDetails = userDetailsStr ? JSON.parse(userDetailsStr) : null;
    const group_id = userDetails?.data?.group_id;
    const location_id = userDetails?.data?.location_id;

    if (!group_id || !location_id) {
      console.error('Missing user group_id or location_id', userDetails);
      this.notifyService.tellUser({
        success: false,
        data: 'Unable to save: User information not found'
      });
      return;
    }

    // Debug: Log rowData to verify row identity is present
    console.log('autoSave rowData:', rowData);

    // Iterate through changed columns
    const changedKeys = Object.keys(changedData);

    for (const key of changedKeys) {
      // Only process dynamic period columns
      if (!this.isPeriodColumn(key)) {
        continue;
      }

      const columnInfo = this.parseColumnName(key);
      if (!columnInfo) {
        continue;
      }

      const body: any = this.periodMode === 'week'
        ? {
            promotion_group_id: rowData.promotion_group_id,
            buying_group_id: rowData.buying_group_id,
            period_year: columnInfo.year,
            period_month: columnInfo.month,
            period_start_date: this.columnStartDates[key],
            slot_type_code: changedData[key] || null
          }
        : {
            product_code: rowData.product_code,
            buying_group_id: rowData.buying_group_id,
            supplier_code: rowData.supplier_code,
            period_year: columnInfo.year,
            period_month: columnInfo.month,
            slot_type_code: changedData[key] || null
          };

      // Debug: Log the body being sent to API
      console.log('Sending to API:', body);

      const updatePromise = new Promise(async (resolve, reject) => {
        this.crudService.insertData(`${this.apiUrl}slotting-board-data`, body)
          .pipe(
            catchError((error: any) => {
              this.notifyService.tellUser({
                success: false,
                data: error?.error?.message || 'Error saving slot assignment'
              });
              console.error('Error saving slot:', error);
              return throwError(error);
            })
          )
          .subscribe(
            result => {
              resolve(result);
            },
            error => {
              console.error('Save failed:', error);
              reject(error);
            }
          );
      });

      updates.push(updatePromise);
    }

    try {
      // Wait for all updates to complete
      await Promise.all(updates);

      // Update local slottingBoardData to reflect changes
      for (const key of changedKeys) {
        if (!this.isPeriodColumn(key)) {
          continue;
        }
        const columnInfo = this.parseColumnName(key);
        if (columnInfo) {
          this.updateSlottingBoardData(
            rowData,
            columnInfo.year,
            columnInfo.month,
            changedData[key]
          );
        }
      }

      // Commit the changes to the grid without reloading all data
      this.dataGrid?.instance.saveEditData();

      // Update isSlotted on the actual grid row data (not a detached copy)
      const mergedRow = { ...rowData, ...changedData };
      const newIsSlotted = this.recalcIsSlotted(mergedRow);
      const gridDataSource = this.dataGrid?.instance.getDataSource();
      if (gridDataSource) {
        const items = gridDataSource.items();
        const gridRow = items.find((item: any) => this.periodMode === 'week'
          ? (item.promotion_group_id === rowData.promotion_group_id
              && item.buying_group_id === rowData.buying_group_id)
          : (item.product_code === rowData.product_code
              && item.buying_group_id === rowData.buying_group_id
              && item.supplier_code === rowData.supplier_code));
        if (gridRow) {
          gridRow.isSlotted = newIsSlotted;
        }
      }
      // Re-apply slot filter if active (isSlotted may have changed)
      if (this.currentSlotFilter.id !== SlotFilter.ShowAll) {
        this.applySlotFilter(this.currentSlotFilter);
      }

      this.notifyService.tellUser({
        success: true,
        data: 'Slot assignment saved successfully'
      });
    } catch (error) {
      console.error('Error during auto-save:', error);
      // On error, revert the changes in the grid
      this.dataGrid?.instance.cancelEditData();
    } finally {
    }
  }

  // Update local slottingBoardData when slot assignments change
  private updateSlottingBoardData(
    rowData: any,
    year: number,
    month: number,
    slotTypeCode: string | null
  ) {
    const existingIndex = this.slottingBoardData.findIndex((slot: any) => {
      if (slot.period?.year !== year || slot.period?.month !== month) return false;
      if (this.periodMode === 'week') {
        return slot.promotion_group_id === rowData.promotion_group_id
            && slot.buying_group_id === rowData.buying_group_id;
      }
      return slot.product_code === rowData.product_code
          && slot.buying_group_id === rowData.buying_group_id
          && slot.supplier_code === rowData.supplier_code;
    });

    if (slotTypeCode) {
      const newEntry: any = this.periodMode === 'week'
        ? {
            promotion_group_id: rowData.promotion_group_id,
            buying_group_id: rowData.buying_group_id,
            slot_type_code: slotTypeCode,
            period: { year, month }
          }
        : {
            product_code: rowData.product_code,
            buying_group_id: rowData.buying_group_id,
            supplier_code: rowData.supplier_code,
            slot_type_code: slotTypeCode,
            period: { year, month }
          };

      if (existingIndex >= 0) {
        this.slottingBoardData[existingIndex] = newEntry;
      } else {
        this.slottingBoardData.push(newEntry);
      }
    } else {
      // Remove entry if slot type code is cleared
      if (existingIndex >= 0) {
        this.slottingBoardData.splice(existingIndex, 1);
      }
    }
  }

  // Parse column name to extract period information
  parseColumnName(columnName: string): { year: number, month: number } | null {
    const monthMatch = columnName.match(/^month_(\d{4})_(\d{1,2})$/);
    if (monthMatch) {
      return {
        year: parseInt(monthMatch[1], 10),
        month: parseInt(monthMatch[2], 10)
      };
    }

    const weekMatch = columnName.match(/^week_(\d{4})_(\d{1,2})$/);
    if (weekMatch) {
      return {
        year: parseInt(weekMatch[1], 10),
        month: parseInt(weekMatch[2], 10)
      };
    }

    return null;
  }

  // ISO-Monday of the given date, plus weekStartIndex days.
  // weekStartIndex is a day offset from ISO Monday (0 = Mon, 2 = Wed, 6 = Sun).
  private getWeekStart(date: Date, weekStartIndex: number): Date {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const offsetFromMonday = (d.getDay() + 6) % 7; // getDay: 0=Sun..6=Sat → 0=Mon..6=Sun
    d.setDate(d.getDate() - offsetFromMonday + weekStartIndex);
    return d;
  }

  // Standard ISO-8601 week number. Returns isoYear + isoWeek because ISO year can differ
  // from calendar year at boundaries (e.g. Mon 2025-12-29 is ISO 2026-W01).
  private getIsoYearWeek(date: Date): { isoYear: number; isoWeek: number } {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const isoYear = d.getUTCFullYear();
    const yearStart = new Date(Date.UTC(isoYear, 0, 1));
    const isoWeek = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    return { isoYear, isoWeek };
  }

  private toIsoDate(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // Open create promotion popup
  openCreatePromotion() {
    this.createPromotion = true;
  }

  // Matches dataFields produced by generateColumns(): month_YYYY_M / month_YYYY_MM and
  // week_YYYY_W / week_YYYY_WW. Kept tight on purpose so any future static column whose
  // name happens to start with "month_" or "week_" (e.g. month_total) is NOT stripped.
  private static readonly PERIOD_DATAFIELD_REGEX = /^(month|week)_\d{4}_\d{1,2}$/;

  // Remove dynamic period columns (matching PERIOD_DATAFIELD_REGEX) from a persisted
  // DevExtreme state so their stale visibleIndex values can't reorder the freshly-generated
  // columns produced by generateColumns(). Non-period columns (product_code, buying_group_id,
  // supplier_code, etc.) keep their visibleIndex, width, sort and filter state; their rendered
  // position can still shift if a persisted visibleIndex value collides with a freshly-
  // generated period column's slot.
  private stripPeriodColumnsFromState(state: any): void {
    if (!state || !Array.isArray(state.columns)) return;
    state.columns = state.columns.filter((c: any) => {
      const df: string | undefined = c?.dataField;
      return !df || !SlottingBoardV2Component.PERIOD_DATAFIELD_REGEX.test(df);
    });
  }

  loadState = () =>
    {
      setTimeout(() => {
        console.log('loading state');
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) return;
        let state: any;
        try { state = JSON.parse(raw); } catch { return; }
        // Heal stale persisted state from older sessions where period columns
        // (month_* / week_*) carried a visibleIndex that no longer matches the
        // freshly-generated calendar order.
        this.stripPeriodColumnsFromState(state);
        this.dataGrid?.instance.state(state);
      }, 300);
    }

    saveState = (state: any) => {
      if(!this.loading) {
        setTimeout(() => {
          console.log('SaveState');
          // Strip isSlotted filter from persisted state to avoid stale filter on reload
          if (state?.filterValue) {
            const filterStr = JSON.stringify(state.filterValue);
            if (filterStr.includes('isSlotted')) {
              delete state.filterValue;
            }
          }
          // Strip dynamic period columns so their visibleIndex can't survive across sessions
          this.stripPeriodColumnsFromState(state);
          localStorage.setItem(this.storageKey, JSON.stringify(state));
        }, 300);
      }
    }

  // Handle promotion creation
  async handlePromotionCreated(promotionData: any) {
    if (promotionData) {
      try {
        // Close the popup
        this.createPromotion = false;

        // Show success message
        this.notifyService.tellUser({
          success: true,
          data: 'Promotion created successfully'
        });

        // Reload the grid data to show new promotion period
        this.dataGrid?.instance.refresh();
      } catch (error) {
        console.error('Error creating promotion period:', error);
        this.notifyService.tellUser({
          success: false,
          data: 'Failed to create promotion period'
        });
      }
    } else {
      // User cancelled
      this.createPromotion = false;
    }
  }
}
